import { useCallback, useEffect, useState } from "react";
import { Pencil, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import { GestioneCqModal } from "@/components/GestioneCqModal";
import { dateIt, eur, num } from "@/lib/format";

const ANNO = 2026;
const CHIUSI = ["Caricata", "Liquidata", "Non interessata", "Non procedibile"];
const oggi = () => new Date().toISOString().slice(0, 10);

/** Elenco pratiche CQ: tabella completa dei contatti con ricerca e filtro agenzia. */
export default function ElencoCq() {
  const [meta, setMeta] = useState(null);
  const [agenzia, setAgenzia] = useState("");
  const [righe, setRighe] = useState([]);
  const [cerca, setCerca] = useState("");
  const [modal, setModal] = useState(null);

  useEffect(() => { api.get("/cq/meta").then((r) => setMeta(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const carica = useCallback(() => {
    const q = `anno=${ANNO}${agenzia ? `&agenzia=${encodeURIComponent(agenzia)}` : ""}${cerca ? `&search=${encodeURIComponent(cerca)}` : ""}`;
    api.get(`/cq/report?${q}`).then((r) => setRighe(r.data.rows)).catch((e) => toast.error(apiError(e)));
  }, [agenzia, cerca]);
  useEffect(() => { const t = setTimeout(carica, 250); return () => clearTimeout(t); }, [carica]);

  const scaduto = (r) => r.data_ricontatto && r.data_ricontatto <= oggi() && !CHIUSI.includes(r.stato);
  const ricontattiScaduti = righe.filter(scaduto).length;

  return (
    <div data-testid="elenco-cq">
      <SectionTitle sub="Tutte le pratiche CQ inserite · clicca una riga per aprire la scheda e aggiornarla"
        right={
          <div className="flex items-center gap-3">
            <label className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca nominativo o n. pratica…" data-testid="cq-cerca"
                className="pl-7 pr-3 py-2 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:border-[#0018A8] w-72" />
            </label>
            <select value={agenzia} onChange={(e) => setAgenzia(e.target.value)} data-testid="cq-filtro-agenzia"
              className="px-3 py-2 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:border-[#0018A8]">
              <option value="">Tutte le agenzie</option>
              {(meta?.agenzie || []).map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        }>
        Elenco pratiche
      </SectionTitle>

      <Card className="p-0 overflow-hidden" data-testid="cq-elenco">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">
            Contatti · {num(righe.length)}{ricontattiScaduti ? <span className="ml-2 text-[#C8102E]">· {ricontattiScaduti} da ricontattare</span> : ""}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                {["Data", "Agenzia", "Nominativo", "N. pratica", "Origine", "Stato", "Ultimo esito", "Ricontatto", "Caricata", "Liquidata", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {righe.map((r) => (
                <tr key={r.id} data-testid={`cq-riga-${r.id}`} onClick={() => setModal(r)}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                  <td className="px-3 py-2 font-mono whitespace-nowrap">{dateIt(r.data)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.agenzia}</td>
                  <td className="px-3 py-2 font-medium text-slate-900">{r.nominativo}</td>
                  <td className="px-3 py-2 font-mono text-slate-500">{r.numero_pratica || "–"}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${r.origine === "agenzia" ? "bg-slate-100 text-slate-600" : "bg-[#0018A8]/10 text-[#0018A8]"}`}>
                      {r.origine === "agenzia" ? "Agenzia" : "Antonella"}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.stato}</td>
                  <td className="px-3 py-2 text-slate-600 max-w-[260px] truncate" title={r.esito_chiamata || (r.note?.at(-1)?.testo)}>{r.esito_chiamata || r.note?.at(-1)?.testo || "–"}</td>
                  <td className={`px-3 py-2 font-mono whitespace-nowrap ${scaduto(r) ? "text-[#C8102E] font-semibold" : ""}`}>
                    {r.data_ricontatto ? `${dateIt(r.data_ricontatto)}${r.ora_ricontatto ? ` ${r.ora_ricontatto}` : ""}` : "–"}
                  </td>
                  <td className="px-3 py-2 font-mono whitespace-nowrap">{r.caricata ? (r.caricata_importo ? eur(r.caricata_importo) : "SI") : "–"}</td>
                  <td className="px-3 py-2 font-mono whitespace-nowrap">{r.liquidata ? (r.liquidata_il ? dateIt(r.liquidata_il) : "SI") : "–"}</td>
                  <td className="px-3 py-2 text-slate-400"><Pencil size={14} /></td>
                </tr>
              ))}
              {righe.length === 0 && <tr><td colSpan={11} className="px-4 py-8 text-center text-slate-400">Nessun contatto.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <GestioneCqModal open={!!modal} meta={meta} contatto={modal} onClose={() => setModal(null)} onSaved={carica} />
    </div>
  );
}
